import { keepPreviousData, useQuery } from '@tanstack/react-query';

import type { AxiosInstance } from 'axios';
import type { StatusBoardApiEnvelope } from '@/shared/api/status-board';
import type { ConsoleEventsResponse } from './types';

import { useStatusBoardApi } from '@/shared/api/status-board';
import { useRegionEndpoint } from '@/shared/hooks';

export interface GetConsoleEventsRequest {
  eventStatus?: string;
  fromDate?: string;
  limit?: number;
  page?: number;
  sort?: string;
  title?: string;
  toDate?: string;
}

/**
 * Status Board 이벤트 목록을 조회한다.
 * @description
 * - `/api/console/events` 엔드포인트를 호출하고 응답 래퍼의 `data`를 언래핑한다.
 * - `page`는 0부터 시작한다.
 */
export const getConsoleEvents = async (
  instance: AxiosInstance,
  params?: GetConsoleEventsRequest,
): Promise<ConsoleEventsResponse> => {
  const response = await instance.get<StatusBoardApiEnvelope<ConsoleEventsResponse>>(
    '/api/console/events',
    { params },
  );

  return response.data.data;
};

/**
 * 이벤트 목록 조회 React Query 훅.
 * @description
 * - 서버 사이드 페이지네이션을 사용한다.
 * @example
 * const { data, isLoading, refetch } = useGetConsoleEvents({ page: 0, limit: 20 });
 */
const useGetConsoleEvents = (params?: GetConsoleEventsRequest) => {
  const { instance } = useStatusBoardApi();
  const { currentRegion } = useRegionEndpoint();

  return useQuery({
    queryKey: ['useGetConsoleEvents', currentRegion, params ?? {}],
    queryFn: () => getConsoleEvents(instance, params),
    placeholderData: keepPreviousData,
  });
};

export default useGetConsoleEvents;
